import { masters } from "@nexseed/shared";
import { Handshake } from "lucide-react";
import { defineMaster } from "../spec";

const status = { id: "isActive", header: "Status", kind: "status" } as const;
const activeSwitch = {
  name: "isActive",
  label: "Active",
  type: "switch",
} as const;

/** What a vendor does, as the vendor form, the list filter and the Business column name it. */
const NATURES_OF_BUSINESS = [
  { value: "manufacturer", label: "Manufacturer" },
  { value: "trader", label: "Trader" },
  { value: "reseller", label: "Reseller" },
  { value: "service_provider", label: "Service provider" },
];

export const vendors = defineMaster({
  key: "vendors",
  title: "Vendors",
  singular: "Vendor",
  description:
    "Seed suppliers, production organisers and traders. Core-synced vendors keep their name and code from VNR Core; vendors added here stay editable.",
  icon: Handshake,
  schema: masters.vendor.create,
  searchPlaceholder: "Search name, code or phone",
  defaultSort: "name",
  coreSynced: "vendors",
  allowCustom: true,
  createPath: "/masters/vendors/new",
  listFilter: {
    param: "natureOfBusiness",
    label: "Nature of business",
    allLabel: "All vendors",
    options: [
      ...NATURES_OF_BUSINESS,
      // Stores ▸ Masters ▸ Vendors opens the list here: the consumables suppliers.
      {
        value: "stores",
        label: "Stores vendors",
        query: { storesVendor: "true" },
        // Stores vendors are kept here, not in VNR Core (user, 2026-10-09).
        noCoreSync: true,
        // New from here is a Stores vendor, its nature locked.
        createSearch: { stores: 1 },
        importer: {
          path: "/stores/import/vendors",
          noun: "Stores vendors",
          help: "One vendor per row, made a Stores vendor with its registered address. A vendor already in the Vendor master (same name or GSTIN) is switched to a Stores vendor instead of being added twice.",
          columns: [
            { name: "name", required: true, note: "The vendor's name." },
            { name: "contact_person", note: "Who to ask for." },
            {
              name: "mobile",
              note: "10-digit mobile, or + country code and number abroad.",
            },
            { name: "email", note: "Email address." },
            { name: "gstin", note: "15-character GSTIN (vendors in India)." },
            { name: "pan", note: "10-character PAN." },
            { name: "vendor_code", note: "Your own code for the vendor." },
            { name: "address", required: true, note: "Street, area." },
            { name: "city", required: true, note: "City, town or village." },
            {
              name: "pincode",
              required: true,
              note: "6-digit PIN (a postcode abroad, optional).",
            },
            {
              name: "state",
              required: true,
              note: "The state, spelt out (Gujarat). A region abroad.",
            },
            { name: "country", note: "Blank means India." },
            { name: "status", note: "Active or Inactive. Blank means Active." },
          ],
          sample: [
            [
              "Shree Packaging",
              "Ramesh",
              "9876543210",
              "",
              "",
              "",
              "",
              "Plot 4, GIDC",
              "Vapi",
              "396195",
              "Gujarat",
              "",
              "",
            ],
          ],
          invalidates: ["vendors"],
        },
      },
    ],
  },
  detailPath: "/masters/vendors/$id",
  formSize: "lg",
  columns: [
    { id: "name", header: "Name", sortable: true },
    {
      id: "natureOfBusiness",
      header: "Business",
      cell: (r) =>
        NATURES_OF_BUSINESS.find((n) => n.value === r.natureOfBusiness)
          ?.label ?? "—",
    },
    { id: "storesVendor", header: "Stores", kind: "bool" },
    { id: "source", header: "Source" },
    status,
  ],
  fields: [
    { name: "name", label: "Name", type: "text", required: true, colSpan: 2 },
    { name: "vendorCode", label: "Vendor code", type: "text" },
    {
      name: "natureOfBusiness",
      label: "Nature of business",
      type: "enum",
      nullable: true,
      options: NATURES_OF_BUSINESS,
    },
    { name: "headName", label: "Contact person", type: "text" },
    // Plain text, not the 10-digit mobile box: a vendor abroad is reached on "+66 81 234 5678".
    {
      name: "phone",
      label: "Phone",
      type: "text",
      placeholder: "10-digit mobile, or +country code abroad",
    },
    {
      name: "secondaryPhone",
      label: "Alternate phone",
      type: "text",
      placeholder: "10-digit mobile, or +country code abroad",
    },
    { name: "email", label: "E-mail", type: "email" },
    { name: "gstNumber", label: "GSTIN", type: "text", transform: "upper" },
    { name: "panNumber", label: "PAN", type: "text", transform: "upper" },
    {
      name: "suspendedUntil",
      label: "Suspended until",
      type: "datetime",
      description: "Suspended vendors cannot be chosen on new arrivals",
    },
    {
      name: "storesVendor",
      label: "Stores vendor",
      type: "switch",
      description:
        "Supplies the store (stickers, rolls, bags…): listed on Stores ▸ Arrival from Vendor",
      localOnCore: true,
    },
    activeSwitch,
  ],
});
